import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Upload, Gauge, MessageSquare, Music4, ArrowRight, ArrowLeft, X, Sparkles, HeartHandshake, SlidersHorizontal } from "lucide-react";

const SEEN_KEY = "spectraloom_onboarding_seen";
const OPEN_EVENT = "spectraloom:open-tour";

export function hasSeenOnboarding() {
  try {
    return localStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

export function markOnboardingSeen() {
  try {
    localStorage.setItem(SEEN_KEY, "1");
  } catch {
    /* ignore */
  }
}

/** Imperative opener used by the "Replay tour" header button. Clears the
 *  seen flag and dispatches a global event that OnboardingHost listens to. */
export function openOnboardingTour() {
  try {
    localStorage.removeItem(SEEN_KEY);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export { OPEN_EVENT };

/**
 * 3-slide onboarding tour shown once per user (localStorage-flagged).
 * Triggered from Home.jsx the first time an authenticated user lands on the
 * page. Keyboard-friendly (Esc to skip), fully i18n.
 */
export default function OnboardingTour({ userName = "", onFinish }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);

  const slides = [
    {
      Icon: Upload,
      color: "from-blue-500 to-cyan-500",
      title: t("onboarding.slide_1_title"),
      body: t("onboarding.slide_1_body"),
    },
    {
      Icon: Music4,
      color: "from-amber-500 to-orange-500",
      title: t("onboarding.slide_genre_title"),
      body: t("onboarding.slide_genre_body"),
    },
    {
      Icon: Gauge,
      color: "from-purple-500 to-pink-500",
      title: t("onboarding.slide_2_title"),
      body: t("onboarding.slide_2_body"),
    },
    {
      Icon: MessageSquare,
      color: "from-emerald-500 to-teal-500",
      title: t("onboarding.slide_3_title"),
      body: t("onboarding.slide_3_body"),
    },
    {
      Icon: SlidersHorizontal,
      color: "from-indigo-500 to-purple-500",
      title: t("onboarding.slide_preview_title"),
      body: t("onboarding.slide_preview_body"),
    },
    {
      Icon: HeartHandshake,
      color: "from-rose-500 to-amber-500",
      title: t("onboarding.slide_human_title"),
      body: t("onboarding.slide_human_body"),
    },
  ];

  const total = slides.length;
  const current = slides[step];
  const isLast = step === total - 1;

  const finish = () => {
    markOnboardingSeen();
    onFinish?.();
  };

  const next = () => (isLast ? finish() : setStep((s) => s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      onKeyDown={(e) => {
        if (e.key === "Escape") finish();
      }}
      data-testid="onboarding-tour"
    >
      <div className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-[color:var(--bg-elev)] shadow-2xl overflow-hidden">
        <button
          onClick={finish}
          className="absolute top-3 right-3 text-[color:var(--fg-muted)] hover:text-white transition-colors z-10"
          aria-label={t("onboarding.skip")}
          data-testid="onboarding-skip"
        >
          <X size={18} />
        </button>

        <div className="px-8 pt-9 pb-7">
          {step === 0 && userName && (
            <div className="tiny-caps text-[color:var(--brand)] mb-3 flex items-center gap-1.5">
              <Sparkles size={11} />
              {t("onboarding.welcome", { name: userName.split(" ")[0] || userName })}
            </div>
          )}

          <div
            className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${current.color} flex items-center justify-center mb-5 shadow-lg`}
          >
            <current.Icon size={26} className="text-white" />
          </div>

          <h2
            className="font-display text-2xl sm:text-3xl font-bold text-white leading-tight mb-3"
            data-testid={`onboarding-title-${step}`}
          >
            {current.title}
          </h2>
          <p className="text-sm text-[color:var(--fg-secondary)] leading-relaxed">
            {current.body}
          </p>
        </div>

        <div className="px-8 pb-7 pt-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5" data-testid="onboarding-dots">
            {slides.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === step
                    ? "w-6 bg-[color:var(--brand)]"
                    : "w-1.5 bg-white/20"
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                onClick={prev}
                className="btn-ghost text-sm flex items-center gap-1.5 !py-2 !px-3"
                data-testid="onboarding-prev"
              >
                <ArrowLeft size={14} />
                <span className="hidden sm:inline">{t("onboarding.prev")}</span>
              </button>
            )}
            <button
              onClick={next}
              className="btn-primary text-sm flex items-center gap-1.5 !py-2 !px-4"
              data-testid={isLast ? "onboarding-finish" : "onboarding-next"}
            >
              <span>{isLast ? t("onboarding.finish") : t("onboarding.next")}</span>
              {!isLast && <ArrowRight size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
